// 2026-05-26 - FormuLearning - Step 1 카드 컨테이너
// 2026-09-09 수정 - 이슈 #72(M901-05): **재검색 확인창에 프로젝트명을 앞세운다.**
//   260901 신고 «프로젝트 전환 시 데이터 혼입»은 혼입이 아니었다 — DB 실증 결과
//   같은 프로젝트에서 실행한 재검색이었다(등록부 M901 §2.1). 문제는 착각을 부르는
//   구조다. ① 입력창이 `<form>` 안이라 **Enter 가 곧 검색**이고 ② 입력창 값은
//   프로젝트가 아니라 **화면에 남으며** ③ 확인창이 **어느 프로젝트인지 말하지 않았다.**
//   프로젝트를 착각한 상태에서는 종전 문구가 경고 구실을 못 한다.
// 2026-09-17 수정 - 이슈 #79(M915-03): **검색 중 잠금 + Break(중단) + 원복.**
//   종전에는 진행 중 스트림을 끊는 수단이 없어, 카드를 접었다 펴거나 프로젝트를 오간 뒤(리마운트) 다시 검색하면
//   서버에서 계속 돌던 직전 검색이 늦게 끝나 **저장본을 덮었다**(260917 실측 R1). 검색 중 Enter 는 제출 버튼이
//   disabled 라 브라우저가 이미 막고 있었다(실측 R2). 대조약 덮임(실측 RD)은 `RldSection` 에서 막는다.
//   이제 검색마다 ID 를 발급해 ① 진행 중에는 새 검색·복원·RLD 선택을 받지 않고 ② Break 는 서버에 그 ID 의
//   무효 처리를 요청해 응답(`cancelled`/`completed`)을 받은 뒤 fetch 를 끊고 저장본 재조회로 화면을 교체하며
//   ③ 프로젝트 전환·언마운트 시에도 끊는다. 현재 검색이 아닌 스트림의 이벤트·동기화·`finally` 는 버린다.
// 2026-09-23 수정 - 이슈 #86: 대조약 선택 시 상세 보강에 `item_seq` 전달 — 목록 쪽 보강과 같은 요청을 공유한다.
// 2026-09-23 수정 - 이슈 #87: **스트림이 종결 이벤트 없이 끝나면(연결 끊김) 알리고 저장본으로 원복한다.**
//   종전에는 네트워크 오류를 다시 던지기만 해 알림 없이 패널 일부만 채워진 채 남았다(저장본과 어긋남).
//   Break 의 원복 경로(서버에 ID 무효 요청 → fetch 중단 → 저장본 재조회)를 `settleStopped` 로 떼어 함께 쓴다.
// 2026-09-23 수정 - 이슈 #82: 검색 진행 중을 전역(`busyStore`)에 알려 **카드 밖 조작을 잠근다**(다른 스텝·Save and continue·
//   프로젝트 전환·Autosave 토글). 헤더의 진행 표시·Break 는 이 카드의 `handleBreak` 를 부른다.
// 2026-11-03 수정 - 이슈 #105(M1002-21): 식약처 특허정보 패널(`mfds_patents`) — KIPRIS 표 아래 별도 표.
// 2026-09-23 수정 - 이슈 #73: 허가 품목 병합 수집 결과로 화면 목록을 바꾼다(`RldSection` `onListReplaced`).
// 2026-09-24 수정 - 이슈 #75(G4): **대조약 선택 시 X 재구성**(`M901-14`, `BR-S3-10`) — Step 3 X 가 있으면
//   «갱신하시겠습니까?» 를 묻고 확인이면 갈아끼운다(취소면 선택만). 반영 실패를 삼키지 않고 알린다.
//   **주성분 확정**(`M915-04`, BR-STEP-03) — 확정 시각이 있으면 재검색·이력 복원을 막고 «새 프로젝트» 를
//   안내한다(화면 판정, 서버 비강제). 대조약 선택은 막지 않는다. Step 3 에서 확정하면 `useSubstanceSync` 로 받는다.
// 파일 위치: frontend/src/features/step1/Step1Card.tsx

import { useEffect, useRef, useState, useCallback } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { useSummaryStore } from '../../stores/summaryStore'
import { useMoleculeStore } from '../../stores/moleculeStore'
import { getStep1, searchStep1Stream, updateStep1, listSnapshots, restoreSnapshot, getSnapshot, fetchRldDetail, newSearchId, cancelStep1Search } from '../../api/step1'
import type { SearchInputType } from '../../api/step1'
import type { Step1Data, PatentMeta, SnapshotListItem, MfdsPatents } from '../../api/step1'
import { applyRldToStep3 } from '../step3/rldToStep3'
import { useStep3Sync } from '../../stores/step3SyncStore'
import { useSubstanceSync } from '../../stores/substanceSyncStore'
import { toast } from '../../stores/toastStore'
import { useBusyStore } from '../../stores/busyStore'
import SearchSection from './SearchSection'
import SearchHistory from './SearchHistory'
import MoleculeSection from './MoleculeSection'
import ManufacturerSection from './ManufacturerSection'
import RldSection from './RldSection'
import PatentSection from './PatentSection'
import MfdsPatentSection from './MfdsPatentSection'
import AdmeSection from './AdmeSection'

/** 진행 중 검색 1건(#79). `breaking` 이 되면 그 뒤 도착하는 이벤트는 화면에 반영하지 않는다. */
interface ActiveSearch {
  id: string
  projectId: string
  controller: AbortController
  breaking: boolean
}

// 주성분 확정 뒤 재검색·이력 복원 시도 안내(BR-STEP-03, #75)
const SUBSTANCE_FIXED_NOTICE =
  '주성분이 확정된 프로젝트입니다(Step 3 저장 시 확정).\n다른 주성분으로 진행하려면 새 프로젝트를 만드세요.'

export default function Step1Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  const projectName = useProjectStore((s) => s.currentProject?.name)
  const [data, setData] = useState<Step1Data | null>(null)
  const [searching, setSearching] = useState(false)
  const [breaking, setBreaking] = useState(false)
  // 진행 중 검색(#79) — 상태가 아니라 ref 인 이유: 스트림 콜백·finally 가 «지금도 내 검색인가» 를
  // 렌더와 무관하게 즉시 판정해야 한다. null 이면 검색 중이 아니다.
  const activeRef = useRef<ActiveSearch | null>(null)
  const [pubchemExtra, setPubchemExtra] = useState<Record<string, unknown> | null>(null)
  const [sources, setSources] = useState<string[]>([])
  // 점진적 표시(이슈 #4) — 현재 진행 단계 + 패널별 수신 완료 여부
  const [stage, setStage] = useState<{ label: string; index: number; total: number } | null>(null)
  const [completed, setCompleted] = useState<Record<string, boolean>>({})
  // 정보/뷰 분리(이슈 #6) — 검색 스냅샷 이력
  const [snapshots, setSnapshots] = useState<SnapshotListItem[]>([])
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const setSummary = useSummaryStore((s) => s.setSummary)
  const publishSmiles = useMoleculeStore((s) => s.setSmiles)
  // 주성분 확정(BR-STEP-03, #75) — 검색·복원 핸들러가 렌더와 무관하게 판정하도록 ref 로도 든다.
  const substanceFixed = !!data?.substance_fixed_at
  const substanceFixedRef = useRef(substanceFixed)
  useEffect(() => { substanceFixedRef.current = substanceFixed })

  // Step 3 에서 주성분을 확정하면 확정 시각만 다시 읽는다(검색 결과 등 나머지 화면은 그대로).
  const substanceRevision = useSubstanceSync((s) => s.revision)
  useEffect(() => {
    if (!projectId || substanceRevision === 0) return
    let cancelled = false
    getStep1(projectId).then((fresh) => {
      if (!cancelled) setData((d) => (d ? { ...d, substance_fixed_at: fresh.substance_fixed_at } : d))
    }).catch(() => { /* 다음 조회에서 맞춰진다 */ })
    return () => { cancelled = true }
  }, [projectId, substanceRevision])

  // 2026-07-18 - 이슈 #40: Step1 영속 AI Summary(검색 시 Gemini 생성)를 발행. 표시는 CardShell 위젯이 전담.
  useEffect(() => {
    setSummary(1, data?.ai_summary, data?.ai_summary_ko)
  }, [data?.ai_summary, data?.ai_summary_ko, setSummary])

  // 2026-08-04 - 이슈 #56: 분자 식별(SMILES)을 게시. Step2 의 물성 조회 버튼이 이를 구독한다.
  // 초기 로드·검색 스트림(molecule 패널)·이력 복원이 모두 data 를 거치므로 한 곳에서 발행하면 된다.
  useEffect(() => {
    publishSmiles(data?.canonical_smiles ?? null)
  }, [data?.canonical_smiles, publishSmiles])

  // 프로젝트 전환·언마운트 시 진행 중 검색을 끊는다(#79). 화면을 되돌릴 대상이 사라지므로 응답은 기다리지 않는다.
  // 진행 표시도 함께 걷는다 — 검색 중이 아니면(activeRef 가 null) 이 상태들은 이미 초기값이다.
  useEffect(() => {
    return () => {
      const search = activeRef.current
      if (!search) return
      activeRef.current = null
      void cancelStep1Search(search.projectId, search.id).catch(() => {})
      search.controller.abort()
      setSearching(false)
      setBreaking(false)
      setStage(null)
      setCompleted({})
    }
  }, [projectId])

  useEffect(() => {
    if (!projectId) return
    // G3(이슈 #33) — pubchem_extra·sources는 step1_data 뷰에 없고 스냅샷에만 있으므로, getStep1이
    // 현재 스냅샷을 조인해 함께 실어준다. 새로고침 시 출처 배지·PubChem 확장정보가 유지되도록 하이드레이트.
    getStep1(projectId).then((d) => {
      setData(d)
      setPubchemExtra((d.pubchem_extra as Record<string, unknown>) ?? null)
      setSources(d.sources ?? [])
    })
    listSnapshots(projectId).then(setSnapshots)
  }, [projectId])

  const handleRestore = useCallback(async (snapshotId: string) => {
    if (!projectId || activeRef.current) return
    if (substanceFixedRef.current) { window.alert(SUBSTANCE_FIXED_NOTICE); return } // BR-STEP-03
    setRestoringId(snapshotId)
    try {
      // 복원: DB 뷰를 스냅샷으로 되돌리고(반환=Step1Data), 뷰 부가정보(sources·pubchem_extra)는
      // step1_data에 영속되지 않으므로 스냅샷 단건에서 가져와 함께 복원한다.
      const [updated, snap] = await Promise.all([
        restoreSnapshot(projectId, snapshotId),
        getSnapshot(projectId, snapshotId),
      ])
      setData(updated)
      setSources(snap.sources ?? [])
      setPubchemExtra((snap.molecule?.pubchem_extra as Record<string, unknown>) ?? null)
      setSnapshots(await listSnapshots(projectId))
    } finally {
      setRestoringId(null)
    }
  }, [projectId])

  // 멈춘 검색의 마무리(#79 Break · #87 연결 끊김 공통) — 서버에 그 ID 의 무효 처리를 요청해 저장 여부를 받고,
  // fetch 를 끊은 뒤 저장본 재조회로 화면을 교체한다. 호출 전에 `search.breaking` 을 세워 이 검색의 이벤트를 버리게 한다.
  const settleStopped = useCallback(async (search: ActiveSearch, reason: 'break' | 'lost') => {
    let status: 'cancelled' | 'completed' | 'unknown' = 'unknown'
    try {
      // 서버가 이 ID 를 무효 처리해야 저장이 막힌다. 저장 중이었다면 끝난 뒤 `completed` 로 답한다.
      status = await cancelStep1Search(search.projectId, search.id)
    } catch {
      /* 중단 요청 실패 — fetch 를 끊고(서버 작업도 연결 종료로 멈춘다) 저장본을 그대로 보인다 */
    }
    search.controller.abort()
    try {
      const [updated, snaps] = await Promise.all([getStep1(search.projectId), listSnapshots(search.projectId)])
      if (activeRef.current !== search) return // 응답을 기다리는 사이 프로젝트가 바뀌었다
      // 비우지 않고 교체한다 — 깜빡임 없이 저장본으로 돌아간다.
      setData(updated)
      setPubchemExtra((updated.pubchem_extra as Record<string, unknown>) ?? null)
      setSources(updated.sources ?? [])
      setSnapshots(snaps)
      const message = reason === 'break'
        ? status === 'completed'
          ? '중단 요청 전에 저장이 끝나 새 검색 결과를 표시합니다.'
          : status === 'cancelled'
            ? '검색을 중단하고 이전 결과로 되돌렸습니다.'
            : '검색을 중단했습니다. 서버 응답을 받지 못해 현재 저장된 결과를 표시합니다.'
        : status === 'completed'
          ? '검색 연결이 끊겼지만 저장은 끝나 새 검색 결과를 표시합니다.'
          : status === 'cancelled'
            ? '검색 연결이 끊겨 결과가 저장되지 않았습니다. 이전 결과로 되돌렸습니다.'
            : '검색 연결이 끊겼습니다. 서버 응답을 받지 못해 현재 저장된 결과를 표시합니다.'
      const tone = reason === 'break' ? (status === 'unknown' ? 'warn' : 'info') : (status === 'completed' ? 'info' : 'warn')
      toast(message, tone, reason === 'lost' ? 8000 : undefined)
    } finally {
      if (activeRef.current === search) {
        activeRef.current = null
        setBreaking(false)
        setSearching(false)
        setStage(null)
        setCompleted({})
      }
    }
  }, [])

  const handleSearch = useCallback(async (query: string, inputType: SearchInputType = 'name') => {
    // 진행 중 잠금(#79) — 버튼·Enter 는 화면에서 막지만, 어느 경로로 들어와도 여기서 한 번 더 막는다.
    if (!projectId || activeRef.current) return
    if (substanceFixedRef.current) { window.alert(SUBSTANCE_FIXED_NOTICE); return } // BR-STEP-03
    // 재검색 경고 — 현재 뷰가 새 결과로 대체됨(원본은 검색 이력 스냅샷에 보존, 이슈 #6)
    // 2026-09-09(#72, M901-05): **프로젝트명을 맨 앞에** 둔다. 무엇이 대체되는지보다
    //   **어느 프로젝트가** 대체되는지가 먼저다 — 착각의 자리가 거기다.
    if (data?.search_input) {
      const ok = window.confirm(
        `［${projectName ?? '현재'}］ 프로젝트의 Step 1 을 다시 검색합니다.\n\n` +
          `현재 "${data.search_input}" 결과가 "${query}" 결과로 대체됩니다.\n` +
          '원본은 검색 이력에 보존되어 나중에 복원할 수 있습니다.\n\n계속할까요?',
      )
      if (!ok) return
    }
    const search: ActiveSearch = { id: newSearchId(), projectId, controller: new AbortController(), breaking: false }
    activeRef.current = search
    // 이 검색이 아직 화면의 주인인가 — Break 확인 뒤·전환 뒤에 도착한 것은 버린다.
    const live = () => activeRef.current === search && !search.breaking
    // 서버가 종결 이벤트(done·cancelled·error)를 보냈는가 — 없이 끝났으면 연결이 끊긴 것이다(#87).
    let ended = false
    setSearching(true)
    setStage(null)
    setCompleted({})
    setPubchemExtra(null)
    setSources([])
    // 패널을 비워 점진적으로 채워지는 모습을 보이게 함(search_input은 즉시 설정).
    setData((prev) =>
      prev
        ? {
            ...prev,
            search_input: query,
            resolved_en: null,
            resolved_ko: null,
            common_name: null,
            iupac_name: null,
            canonical_smiles: null,
            inchi: null,
            cas_number: null,
            molecular_image: null,
            manufacturers: [],
            rld_list: [],
            patents: [],
            patent_meta: {},
            mfds_patents: null,
            adme: {},
            ai_summary: null,
          }
        : prev,
    )
    try {
      await searchStep1Stream(projectId, query, (ev) => {
        if (!live()) return
        const d = ev.data
        if (ev.event === 'done' || ev.event === 'cancelled' || ev.event === 'error') ended = true
        if (ev.event === 'stage') {
          setStage({ label: d.label as string, index: d.index as number, total: d.total as number })
          return
        }
        if (ev.event === 'error') {
          window.alert((d.message as string) || '검색 처리 중 오류가 발생했습니다.')
          return
        }
        if (ev.event === 'panel') {
          if (Array.isArray(d.sources)) setSources(d.sources as string[])
          setCompleted((prev) => ({ ...prev, [d.panel as string]: true }))
          setData((prev) => {
            if (!prev) return prev
            switch (d.panel) {
              case 'resolve':
                return { ...prev, resolved_en: (d.resolved_en as string) ?? null, resolved_ko: (d.resolved_ko as string) ?? null }
              case 'molecule': {
                const mol = (d.molecule as Record<string, string | null>) ?? {}
                setPubchemExtra((d.pubchem_extra as Record<string, unknown>) ?? null)
                return { ...prev, ...mol }
              }
              case 'rld':
                return { ...prev, rld_list: (d.rld_list as Record<string, unknown>[]) ?? [] }
              case 'manufacturers':
                return { ...prev, manufacturers: (d.manufacturers as Record<string, string | null>[]) ?? [] }
              case 'patents':
                return { ...prev, patents: (d.patents as Record<string, string | null>[]) ?? [], patent_meta: (d.patent_meta as PatentMeta) ?? {} }
              case 'mfds_patents':
                return { ...prev, mfds_patents: (d.mfds_patents as MfdsPatents | null) ?? null }
              case 'adme':
                return { ...prev, adme: (d.adme as Record<string, unknown>) ?? {} }
              case 'ai_summary':
                return { ...prev, ai_summary: (d.ai_summary as string) ?? null, ai_summary_ko: (d.ai_summary_ko as string) ?? null }
              default:
                return prev
            }
          })
        }
      }, inputType, { searchId: search.id, signal: search.controller.signal })
      if (!live()) return
      if (!ended) {
        search.breaking = true
        await settleStopped(search, 'lost')
        return
      }
      // 정합성 보정 — 최종 저장본으로 동기화(선택 보존 등) + 이력 갱신(새 스냅샷 반영)
      const [updated, snaps] = await Promise.all([getStep1(projectId), listSnapshots(projectId)])
      if (!live()) return
      setData(updated)
      setSnapshots(snaps)
    } catch {
      if (!live()) return // Break·전환으로 끊긴 스트림의 AbortError — 그쪽 흐름이 화면을 맡는다
      // 네트워크 오류·HTTP 오류(#87) — 알리고 저장본으로 되돌린다
      search.breaking = true
      await settleStopped(search, 'lost')
    } finally {
      if (live()) {
        activeRef.current = null
        setSearching(false)
        setStage(null)
      }
    }
  }, [projectId, projectName, data?.search_input, settleStopped])

  // Break(#79) — 확인 → Breaking(서버 중단 응답 대기) → fetch 중단 → 저장본 재조회로 화면 교체 → 재검색 가능.
  const handleBreak = useCallback(async () => {
    const search = activeRef.current
    if (!projectId || !search || search.breaking) return
    const total = stage?.total ?? 7
    const done = Object.keys(completed).length
    const doing = done >= total ? '결과 저장' : (stage?.label ?? '검색 준비')
    const ok = window.confirm(
      `검색을 중단할까요?\n\n${total}개 중 ${done}개 완료, ${doing} 진행 중입니다.\n` +
        '중단하면 이번 검색 결과는 저장되지 않고 이전 결과로 돌아갑니다.',
    )
    if (!ok || activeRef.current !== search) return
    search.breaking = true
    setBreaking(true)
    await settleStopped(search, 'break')
  }, [projectId, stage, completed, settleStopped])

  // 진행 중 작업 전역 등록(#82) — 카드 밖 조작(다른 스텝·Save and continue·프로젝트 전환)을 잠근다.
  // `searching` 에 묶인 effect 라서 해제가 빠지지 않는다: 종료(완료·Break·연결 끊김·오류)는 false 전환으로,
  // 언마운트(프로젝트 전환·로그아웃·Admin 이동)는 effect 정리로 `end` 가 불린다.
  const handleBreakRef = useRef(handleBreak)
  useEffect(() => { handleBreakRef.current = handleBreak })
  const busyIdRef = useRef<string | null>(null)
  useEffect(() => {
    const id = searching ? activeRef.current?.id : undefined
    if (!id) return
    busyIdRef.current = id
    useBusyStore.getState().begin({ id, step: 1, label: 'Step 1 검색', cancel: () => { void handleBreakRef.current() } })
    return () => {
      busyIdRef.current = null
      useBusyStore.getState().end(id)
    }
  }, [searching])
  useEffect(() => {
    const id = busyIdRef.current
    if (!id) return
    const total = stage?.total ?? 7
    const done = Object.keys(completed).length
    useBusyStore.getState().update(id, {
      phase: breaking ? 'breaking' : 'running',
      progress: { done, total, doing: done >= total ? '결과 저장' : (stage?.label ?? '검색 준비') },
    })
  }, [searching, stage, completed, breaking])

  const handleSelectRld = useCallback(async (idx: number) => {
    if (!projectId || activeRef.current) return
    const updated = await updateStep1(projectId, { selected_rld_id: idx })
    setData(updated)
    const itemSeq = updated.rld_list[idx]?.item_seq as string | undefined
    // 2026-07-09 - RLD 선택 시 게이팅 즉시 갱신 (이슈 #33): Step3 하드 잠금 해제 반영.
    void useProjectStore.getState().refreshGating()
    // G4(이슈 #33) — 선택 즉시 Step3 준비: 상세 보강을 보장한 뒤 총중량·첨가제·주성분을 반영한다.
    // #75(BR-S3-10): X 가 있으면 묻고 확인이면 갈아끼운다 · 취소면 선택만 남는다.
    // 반영 실패는 알린다 — 종전처럼 삼키면 "바꿨는데 미적용" 으로만 보인다(이슈 #75 DoD).
    ;(async () => {
      try {
        // 미보강이면 채워 반환(G1로 영속). 목록 쪽 보강과 같은 품목이면 그 요청을 공유한다(#86)
        const rld = await fetchRldDetail(projectId, idx, itemSeq)
        const applied = await applyRldToStep3(projectId, rld, (msg) => window.confirm(msg))
        if (applied) useStep3Sync.getState().bump() // 함께 마운트된 Step 3 카드가 X·총중량을 다시 읽는다
      } catch {
        toast('대조약 정보를 Step 3 X 변수에 반영하지 못했습니다. 대조약을 다시 선택해 주세요.', 'warn', 6000)
      }
    })()
  }, [projectId])

  if (!data) return <p className="text-xs text-text-secondary">Loading...</p>

  return (
    <div>
      {substanceFixed && (
        <p className="mb-2 rounded px-2.5 py-1.5 text-[11px] text-text-body"
           style={{ border: '1px solid var(--border-soft)', background: 'var(--color-bg-panel)' }}>
          🔒 <b>주성분 확정됨</b> — 재검색·검색 이력 복원을 할 수 없습니다. 다른 주성분으로 진행하려면 새 프로젝트를 만드세요.
          (대조약은 Step 4 로 넘어가기 전까지 다시 고를 수 있습니다.)
        </p>
      )}
      <SearchSection
        searchInput={data.search_input}
        resolvedEn={data.resolved_en}
        resolvedKo={data.resolved_ko}
        onSearch={handleSearch}
        loading={searching}
        breaking={breaking}
        onBreak={handleBreak}
      />

      <SearchHistory
        snapshots={snapshots}
        currentSnapshotId={data.current_snapshot_id ?? null}
        onRestore={handleRestore}
        restoringId={restoringId}
        disabled={searching}
      />

      {searching && stage && (
        <div className="mb-4 rounded-md border p-2.5" style={{ borderColor: 'var(--border-soft)', background: 'var(--color-bg-panel)' }}>
          <div className="flex items-center justify-between text-[11px] text-text-body">
            <span>
              {breaking ? 'Breaking' : 'Collecting information'}<span className="text-accent-purple">{'.'.repeat((stage.index % 3) + 1)}</span>
              {'  '}<span className="text-text-secondary">{stage.label}</span>
            </span>
            <span className="text-[10px] text-text-secondary">{stage.index + 1}/{stage.total}</span>
          </div>
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full" style={{ background: 'var(--border-subtle)' }}>
            <div
              className="h-full rounded-full bg-accent-purple transition-all"
              style={{ width: `${((stage.index + 1) / stage.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {data.search_input && (
        <>
          {/* 2026-07-18 - 이슈 #40: Step1 AI Summary 박스를 공통 위젯(CardShell)으로 이관. 여기선 발행만. */}
          <MoleculeSection
            commonName={data.common_name}
            iupacName={data.iupac_name}
            smiles={data.canonical_smiles}
            casNumber={data.cas_number}
            inchi={data.inchi}
            molecularImage={data.molecular_image}
            pubchemExtra={pubchemExtra}
            source={sources.includes('PubChem') ? 'PubChem' : undefined}
            loading={searching && !completed.molecule}
          />
          <ManufacturerSection manufacturers={data.manufacturers ?? []} loading={searching && !completed.manufacturers} />
          <AdmeSection adme={data.adme ?? {}} loading={searching && !completed.adme} />
          <RldSection rldList={data.rld_list ?? []} selectedRldId={data.selected_rld_id} onSelect={handleSelectRld} loading={searching && !completed.rld} projectId={projectId} locked={searching} onListReplaced={setData} />
          <PatentSection patents={data.patents ?? []} meta={data.patent_meta} loading={searching && !completed.patents} />
          <MfdsPatentSection data={data.mfds_patents ?? null} loading={searching && !completed.mfds_patents} />
        </>
      )}
    </div>
  )
}
